import type {
  BaseResponse,
  ServiceResponseMutate,
} from "~/common/types/api.type";

import {
  useServiceMutationResponse,
  useServiceRequest,
  type ServiceMutationOptions,
} from "~/common/hooks";
import { useMutation } from "~/libs/swr";

/**
 * Generic hook for POST operations
 * @template TRequest - The request payload type
 * @template TResponse - The response type from the API
 * @template TData - The transformed data type (defaults to TResponse)
 */
export const useMutate = <
  TRequest = unknown,
  TResponse = unknown,
  TData = TResponse,
  TParams = TRequest
>(
  endpoint: string,
  options?: ServiceMutationOptions<TRequest, TResponse, TData, TParams>
): ServiceResponseMutate<TRequest, TData> => {
  /** method */
  const method = options?.config?.method || "POST";

  /** useServiceRequest */
  const { key, config } = useServiceRequest(endpoint, method);

  /** Request Config */
  const reqConfig = { ...config, ...options?.config };

  /** useServiceMutationResponse */
  return useServiceMutationResponse(
    useMutation<TRequest, BaseResponse<TResponse>>(key, reqConfig),
    options
  );
};
/**
 * Small factory to create method-specific mutate hooks (DELETE/PUT/POST etc).
 * It preserves any other config fields from caller's options while ensuring the
 * HTTP method is set.
 */
function buildHook(method: string) {
  return function useMethod<
    TRequest = unknown,
    TResponse = unknown,
    TData = TResponse,
    TParams = TRequest
  >(
    endpoint: string,
    options?: ServiceMutationOptions<TRequest, TResponse, TData, TParams>
  ) {
    return useMutate<TRequest, TResponse, TData, TParams>(endpoint, {
      ...options,
      config: {
        ...(options?.config ?? {}),
        method,
      },
    });
  };
}

export const useDelete = buildHook("DELETE");
export const usePut = buildHook("PUT");
export const usePatch = buildHook("PATCH");
export const usePost = buildHook("POST");
export const useMutateGet = buildHook("GET");
